import React from 'react'
import { useTranslation } from 'react-i18next'
import { PrimaryLinkButton, Button } from '../'
import { useMutation, useQuery } from '@apollo/client'

import { getAllAppsQuery, deleteAppQuery } from '../../queries'
import { AllApps } from '../../queries/types/AllApps'
import { DeleteApp } from '../../queries/types/DeleteApp'
import { useHistory } from 'react-router-dom'
import { App } from '../../types/global'
import { ConfirmButton, EmptyState, LoadingRows, MonoId, PageHeader, SecretKey, TableCard, TableHead, TableMessageRow } from '../ui'

const COLUMN_COUNT = 5

export function AppsListing(): JSX.Element {
    const history = useHistory()
    const {t} = useTranslation()

    const { data: apps, loading, refetch } = useQuery<AllApps>(getAllAppsQuery, {
        fetchPolicy: 'cache-and-network'
    })
    const [ deleteByID ] = useMutation<DeleteApp>(deleteAppQuery)

    function deleteApp(app: App) {
        return deleteByID(
            {
                variables: { id: app.id }
            }
        )
    }

    const rows = apps?.apps

    function tableBody() {
        if (!rows && loading) {
            return <TableMessageRow colSpan={COLUMN_COUNT}><LoadingRows message={t('apps.loading')} /></TableMessageRow>
        }
        if (rows && rows.length === 0) {
            return (
                <TableMessageRow colSpan={COLUMN_COUNT}>
                    <EmptyState
                        title={t('apps.empty.title')}
                        subtitle={t('apps.empty.subtitle')}
                        action={<PrimaryLinkButton to='/apps/create'>{t('shared.button.label.create')}</PrimaryLinkButton>}
                    />
                </TableMessageRow>
            )
        }
        return rows?.map((app) => (
            <tr key={app.id} className='border-b border-gray-100 last:border-0 hover:bg-gray-50'>
                <td className='px-4 py-3'><MonoId value={app.id} /></td>
                <td className='px-4 py-3 text-gray-800'>{app.name}</td>
                <td className='px-4 py-3'><SecretKey value={app.key} /></td>
                <td className='px-4 py-3 text-gray-600'><span className='block max-w-xs truncate' title={app.s2sUrl}>{app.s2sUrl}</span></td>
                <td className='px-4 py-3'>
                    <div className='flex flex-row items-center justify-end gap-2'>
                        <Button size='sm' onClick={() => history.push(`/apps/edit/${app.id}`)}>{t('apps.table.actions.edit')}</Button>
                        <ConfirmButton
                            title={t('apps.delete.confirm.title')}
                            message={t('apps.delete.confirm.message', { name: app.name })}
                            confirmLabel={t('apps.table.actions.delete')}
                            onConfirm={() => deleteApp(app).then(() => refetch())}>
                            {t('apps.table.actions.delete')}
                        </ConfirmButton>
                    </div>
                </td>
            </tr>
        ))
    }

    return (
        <>
            <PageHeader title={t('apps.title')}>
                <PrimaryLinkButton to='/apps/create'>{t('shared.button.label.create')}</PrimaryLinkButton>
            </PageHeader>
            <TableCard>
                <TableHead columns={[
                    t('apps.table.titles.id'),
                    t('apps.table.titles.name'),
                    t('apps.table.titles.key'),
                    t('apps.table.titles.s2s'),
                    <span key='actions' className='sr-only'>Actions</span>,
                ]} />
                <tbody>{tableBody()}</tbody>
            </TableCard>
        </>
    )
}
